import { AlertTriangle, Loader2, Sparkles } from 'lucide-react'
import { pluralize } from 'utils/pluralize'
import type { AutofillCardStatus, AutofillFileProblem, AutofillProblem } from '../../hooks/useDocumentAutofill'
import type { CardSummary } from '../../utils/documentAutofill/cardPlan'
import { documentTypeLabel, isRetryableProblem } from '../../utils/documentAutofill/targets'

interface DocumentAutofillStatusProps {
    status: AutofillCardStatus | undefined
    /** The document's name in a sentence, e.g. "voided check or bank letter". */
    label: string
    /** This card's name as the form shows it ("Vehicle Registration"). */
    cardName: string
    /** The name of the card that reads a pipeline doc type, as the form shows it ("Vehicle Insurance"); undefined when no card here reads it. */
    cardNameOf: (docType: string) => string | undefined
    /** Takes a file off this card (a file read as another document), or undefined where it can't be removed from here. */
    removeFileAction?: (sourceId: string) => (() => void) | undefined
    /** Why the form takes nothing from a document now, for a read whose every field was locked. */
    lockedMessage?: string
    /** Stop the read (Cancel); the files stay on the card. */
    onCancel: () => void
    /** Read the files that failed again (Try again). */
    onRetry: () => void
}

const LINK_BUTTON =
    'shrink-0 rounded-3xl px-2 py-0.5 font-medium text-foreground hover:bg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent'

const summaryMessage = (summary: CardSummary, documents: number, lockedMessage: string | undefined): string => {
    const these = documents === 1 ? 'this document' : 'these documents'
    if (summary.found === 0) return `Nothing to fill from ${these}. Fill the fields by hand.`
    const conflicts =
        summary.conflicts > 0
            ? ` The documents disagree on ${pluralize(summary.conflicts, 'field')}: pick the right value under ${summary.conflicts === 1 ? 'it' : 'each'}.`
            : ''
    if (summary.filled > 0) return `Filled ${pluralize(summary.filled, 'field')} from ${these}. Check the highlighted fields.${conflicts}`
    if (summary.differs > 0) return `Read ${these}. Check the highlighted fields.${conflicts}`
    if (summary.conflicts > 0) return conflicts.trim()
    // Nothing written and nothing offered: either every field was locked, or the form already matches.
    if (summary.locked === summary.found) return lockedMessage ?? "Nothing was filled: this form can't take these fields now."
    return `Nothing new to fill: the form already holds what ${documents === 1 ? 'this document shows' : 'these documents show'}.`
}

/** "a voided check", "an insurance card": the article the name needs. */
const withArticle = (label: string) => `${/^[aeiou]/i.test(label) ? 'an' : 'a'} ${label}`

interface CardNames {
    label: string
    cardName: string
    cardNameOf: (docType: string) => string | undefined
}

/**
 * A file read as another document than the card's, named by the card that reads it ("Vehicle Insurance"), else by the document's
 * name. When the answer came from an earlier read of the same file (the server answers by the bytes, whatever card asks), the file
 * was already processed as that document: if it also holds this card's document, that page has to come as its own file.
 */
const wrongDocumentMessage = (problem: AutofillFileProblem, { label, cardName, cardNameOf }: CardNames): string => {
    const readAs = problem.readAs ? (cardNameOf(problem.readAs) ?? documentTypeLabel(problem.readAs)) : undefined
    if (readAs && problem.cached) return `Already processed as ${readAs}. If it also holds the ${cardName}, upload that page as a separate file.`
    if (readAs) return `This is ${readAs}, not ${cardName}. Nothing was filled.`
    return `This doesn't look like ${withArticle(label)}. Nothing was filled.`
}

const PROBLEMS: Record<Exclude<AutofillProblem, 'wrong_document'>, (label: string) => string> = {
    several_documents: () => 'This file holds more than one document. Upload them one at a time to fill the fields.',
    unreadable: () => "Couldn't read this image clearly. Try a sharper photo, or fill the fields by hand.",
    rejected: () => "This file can't be read automatically. Fill the fields by hand.",
    gone: () => "This document can't be found any more. Reload the page to see the profile's documents.",
    timeout: () => "Couldn't read the document in time.",
    failed: () => "Couldn't read the document right now.",
}

const canRetry = ({ problem, retriesLeft }: AutofillFileProblem) => isRetryableProblem(problem) && retriesLeft > 0

/** One file that filled nothing: its name when the card holds several, and the hand-fill advice once its retries are used. */
const problemMessage = (problem: AutofillFileProblem, names: CardNames, named: boolean): string => {
    const retriesUsed = isRetryableProblem(problem.problem) && problem.retriesLeft === 0
    const message = problem.problem === 'wrong_document' ? wrongDocumentMessage(problem, names) : PROBLEMS[problem.problem](names.label)
    return `${named ? `${problem.fileName}: ` : ''}${message}${retriesUsed ? ' Fill the fields by hand.' : ''}`
}

/** The lines under a document card about its AI read: reading (with Cancel), what the files filled, and each file that filled nothing. */
export const DocumentAutofillStatus = ({
    status,
    label,
    cardName,
    cardNameOf,
    removeFileAction,
    lockedMessage,
    onCancel,
    onRetry,
}: DocumentAutofillStatusProps) => {
    if (!status) return null

    if (status.status === 'reading') {
        return (
            <div className="mt-2 flex items-center gap-2 text-xs text-foreground-muted" role="status">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" aria-hidden="true" />
                <span>{status.files === 1 ? 'Reading file…' : `Reading ${status.files} files…`}</span>
                <button
                    type="button"
                    className={`ml-auto ${LINK_BUTTON}`}
                    onClick={(event) => {
                        event.stopPropagation()
                        onCancel()
                    }}
                >
                    Cancel
                </button>
            </div>
        )
    }

    const documents = status.files - status.problems.length
    return (
        <div className="mt-2 space-y-1 text-xs">
            {status.summary && (
                <p className="flex items-start gap-1.5 text-success-soft-foreground" role="status">
                    <Sparkles className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {summaryMessage(status.summary, documents, lockedMessage)}
                </p>
            )}
            {status.problems.length > 0 && (
                <div className="flex items-start gap-1.5 text-warning-soft-foreground" role="alert">
                    <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1 space-y-0.5">
                        {status.problems.map((problem, index) => {
                            // A file read as another document is on the wrong card: one click takes it off.
                            const remove = problem.problem === 'wrong_document' ? removeFileAction?.(problem.sourceId) : undefined
                            return (
                                <span key={`${index}-${problem.fileName}`} className="block break-words">
                                    {problemMessage(problem, { label, cardName, cardNameOf }, status.files > 1)}
                                    {remove && (
                                        <button
                                            type="button"
                                            className="ml-1.5 rounded-md bg-warning-soft px-1.5 font-semibold text-foreground hover:bg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                            aria-label={`Remove ${problem.fileName} from ${cardName}`}
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                remove()
                                            }}
                                        >
                                            Remove from this card
                                        </button>
                                    )}
                                </span>
                            )
                        })}
                    </span>
                    {status.problems.some(canRetry) && (
                        <button
                            type="button"
                            className={LINK_BUTTON}
                            onClick={(event) => {
                                event.stopPropagation()
                                onRetry()
                            }}
                        >
                            Try again
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}
